// views/auth/SignUp.js
import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Checkbox,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    Icon,
    Input,
    InputGroup,
    InputRightElement,
    Text,
    useColorModeValue,
    useToast,
} from "@chakra-ui/react";

import { HSeparator } from "components/separator/Separator";
import DefaultAuth from "layouts/auth/Default";
import illustration from "assets/img/auth/auth.jpg";
import { FcGoogle } from "react-icons/fc";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";

// IMPORT FIREBASE SERVICES
import { signUpService, checkAdminEmail } from "../../../Service/AuthService";

function SignUp() {
    const textColor = useColorModeValue("navy.700", "white");
    const textColorSecondary = "gray.400";
    const textColorDetails = useColorModeValue("navy.700", "secondaryGray.600");
    const textColorBrand = useColorModeValue("brand.500", "white");
    const brandStars = useColorModeValue("brand.500", "brand.400");

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [show, setShow] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [agreeToTerms, setAgreeToTerms] = useState(false);
    const [passwordError, setPasswordError] = useState("");

    const toast = useToast();
    const navigate = useNavigate();

    // Function to convert Firebase error codes to user-friendly messages
    const getErrorMessage = (errorCode) => {
        const errorMessages = {
            "auth/email-already-in-use": "This email is already registered. Please sign in or use a different email.",
            "auth/invalid-email": "Please enter a valid email address.",
            "auth/operation-not-allowed": "Email/password accounts are not enabled. Please contact support.",
            "auth/weak-password": "Your password is too weak. Please use at least 8 characters with a mix of letters and numbers.",
            "auth/network-request-failed": "Network error. Please check your internet connection and try again.",
            "auth/too-many-requests": "Too many attempts. Please try again later.",
            "auth/user-disabled": "This account has been disabled. Please contact support.",
            "auth/requires-recent-login": "Please sign in again to complete this action.",
            "auth/invalid-credential": "Invalid credentials provided. Please check your information.",
            "auth/account-exists-with-different-credential": "An account already exists with this email using a different sign-in method.",
            "auth/internal-error": "An unexpected error occurred. Please try again.",
            "auth/missing-email": "Please enter your email address.",
            "auth/missing-password": "Please enter a password.",
        };

        return errorMessages[errorCode] || "Unable to create account. Please try again or contact support.";
    };

    const validatePassword = () => {
        if (password.length < 8) {
            setPasswordError("Password must be at least 8 characters");
            return false;
        }

        if (password !== confirmPassword) {
            setPasswordError("Passwords do not match");
            return false;
        }

        setPasswordError("");
        return true;
    };

    const handleSignUp = async () => {
        if (!email || !password || !confirmPassword) {
            toast({
                title: "Missing Information",
                description: "Please fill in all required fields to continue.",
                status: "info",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            toast({
                title: "Invalid Email",
                description: "Please enter a valid email address.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        if (!validatePassword()) {
            toast({
                title: "Password Issue",
                description: passwordError,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setLoading(true);

        // Check if email exists in admin collection
        try {
            const isAdminEmail = await checkAdminEmail(email);

            if (!isAdminEmail) {
                setLoading(false);
                toast({
                    title: "Permission Denied",
                    description: "Only authorized admin emails can register for an account.",
                    status: "error",
                    duration: 4000,
                    isClosable: true,
                });
                return;
            }
        } catch (error) {
            setLoading(false);
            toast({
                title: "Verification Error",
                description: "Unable to verify email permissions. Please try again later.",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        // Proceed with sign-up if email exists in admin collection
        const res = await signUpService(email, password);
        setLoading(false);

        if (res.success) {
            toast({
                title: "Welcome!",
                description: "Your account has been created successfully.",
                status: "success",
                duration: 2000,
                isClosable: true,
            });

            navigate("/admin");
        } else {
            // Extract error code from Firebase error string
            let errorCode = res.error;

            // If error is in format "Firebase: Error (auth/error-code)." extract the code
            const match = res.error.match(/\(([^)]+)\)/);
            if (match) {
                errorCode = match[1];
            }

            const userFriendlyMessage = getErrorMessage(errorCode);

            toast({
                title: "Registration Failed",
                description: userFriendlyMessage,
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        }
    };

    // Handle Enter key press
    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            handleSignUp();
        }
    };

    return (
        <DefaultAuth illustrationBackground={illustration} image={illustration}>
            <Flex
                maxW={{ base: "100%", md: "max-content" }}
                w="100%"
                mx={{ base: "auto", lg: "0px" }}
                me="auto"
                h="100%"
                alignItems="start"
                justifyContent="center"
                mb={{ base: "30px", md: "60px" }}
                px={{ base: "25px", md: "0px" }}
                mt={{ base: "40px", md: "14vh" }}
                flexDirection="column"
            >
                <Box me="auto">
                    <Heading color={textColor} fontSize="36px" mb="10px">
                        Sign Up
                    </Heading>
                    <Text mb="36px" ms="4px" color={textColorSecondary}>
                        Create your account to get started!
                    </Text>
                </Box>

                <Flex
                    direction="column"
                    w={{ base: "100%", md: "420px" }}
                    maxW="100%"
                    background="transparent"
                    borderRadius="15px"
                >
                    {/* Email */}
                    <FormControl mb="15px">
                        <FormLabel color={textColor} fontSize="sm" fontWeight="500">
                            Email<Text as="span" color={brandStars}>*</Text>
                        </FormLabel>
                        <Input
                            type="email"
                            variant="auth"
                            placeholder="you@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyPress={handleKeyPress}
                            size="lg"
                        />
                    </FormControl>

                    {/* Password */}
                    <FormControl mb="15px">
                        <FormLabel color={textColor} fontSize="sm" fontWeight="500">
                            Password<Text as="span" color={brandStars}>*</Text>
                        </FormLabel>

                        <InputGroup size="md">
                            <Input
                                type={show ? "text" : "password"}
                                placeholder="Min. 8 characters"
                                variant="auth"
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    if (confirmPassword && e.target.value !== confirmPassword) {
                                        setPasswordError("Passwords do not match");
                                    } else if (e.target.value.length > 0 && e.target.value.length < 8) {
                                        setPasswordError("Password must be at least 8 characters");
                                    } else {
                                        setPasswordError("");
                                    }
                                }}
                                onKeyPress={handleKeyPress}
                                size="lg"
                            />
                            <InputRightElement>
                                <Icon
                                    color={textColorSecondary}
                                    as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                                    onClick={() => setShow(!show)}
                                    cursor="pointer"
                                />
                            </InputRightElement>
                        </InputGroup>
                    </FormControl>

                    {/* Confirm Password */}
                    <FormControl mb="20px" isInvalid={passwordError !== ""}>
                        <FormLabel color={textColor} fontSize="sm" fontWeight="500">
                            Confirm Password<Text as="span" color={brandStars}>*</Text>
                        </FormLabel>

                        <InputGroup size="md">
                            <Input
                                type={showConfirm ? "text" : "password"}
                                placeholder="Confirm your password"
                                variant="auth"
                                value={confirmPassword}
                                onChange={(e) => {
                                    setConfirmPassword(e.target.value);
                                    if (password && e.target.value !== password) {
                                        setPasswordError("Passwords do not match");
                                    } else if (password.length > 0 && password.length < 8) {
                                        setPasswordError("Password must be at least 8 characters");
                                    } else {
                                        setPasswordError("");
                                    }
                                }}
                                onKeyPress={handleKeyPress}
                                size="lg"
                            />
                            <InputRightElement>
                                <Icon
                                    color={textColorSecondary}
                                    as={showConfirm ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                                    onClick={() => setShowConfirm(!showConfirm)}
                                    cursor="pointer"
                                />
                            </InputRightElement>
                        </InputGroup>
                        {passwordError && (
                            <Text color="red.500" fontSize="xs" mt={1}>
                                {passwordError}
                            </Text>
                        )}
                    </FormControl>

                    {/* SIGN-UP BUTTON */}
                    <Button
                        fontSize="sm"
                        variant="brand"
                        w="100%"
                        h="50px"
                        mb="24px"
                        isLoading={loading}
                        onClick={handleSignUp}
                    >
                        Sign Up
                    </Button>

                    <Text color={textColorDetails} fontSize="14px">
                        Already have an account?
                        <NavLink to="/auth/sign-in">
                            <Text as="span" color={textColorBrand} ms="5px" fontWeight="500">
                                Sign In
                            </Text>
                        </NavLink>
                    </Text>
                </Flex>
            </Flex>
        </DefaultAuth>
    );
}

export default SignUp;